/**
 * X (Twitter) Ads pixel for Tagada CRM sign-up attribution.
 * Landing (tagada.io) owns Page View. CRM fires Sign-up after account creation.
 */

export const X_PIXEL_ID = 'ra1ph';
export const X_SIGNUP_EVENT_ID = 'tw-ra1ph-re1g7';

const FLUSH_MS = 400;
const READY_TIMEOUT_MS = 2000;

type TwqClient = ((...args: unknown[]) => void) & {
  exe?: (...args: unknown[]) => void;
  queue?: unknown[];
  version?: string;
};

declare global {
  interface Window {
    twq?: TwqClient;
  }
}

const isTagadaPrimaryHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'app.tagada.io' || host === 'localhost' || host.endsWith('.vercel.app');
};

/** Whitelabel / partner hosts must not fire Tagada ad conversions. */
export const shouldTrackXAds = (): boolean => {
  const brand = (import.meta.env.VITE_BRAND as string | undefined)?.toLowerCase();
  if (brand && brand !== 'tagadapay') return false;
  return isTagadaPrimaryHost();
};

const createTwqStub = (): TwqClient => {
  const queue: unknown[] = [];
  const twq = ((...args: unknown[]) => {
    if (twq.exe) {
      twq.exe(...args);
      return;
    }
    queue.push(args);
  }) as TwqClient;
  twq.version = '1.1';
  twq.queue = queue;
  return twq;
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const waitForTwqReady = async (): Promise<void> => {
  const started = Date.now();
  while (!window.twq?.exe && Date.now() - started < READY_TIMEOUT_MS) {
    await wait(50);
  }
};

const signupDedupeKey = (email?: string | null, conversionId?: string | null): string => {
  const emailKey = email?.trim().toLowerCase();
  return `x_signup_${emailKey || conversionId || 'session'}`;
};

const markSignupTracked = (key: string): boolean => {
  try {
    if (sessionStorage.getItem(key) === '1') return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
};

let didInit = false;

export const initXPixel = (): void => {
  if (typeof document === 'undefined' || didInit || !shouldTrackXAds()) return;
  didInit = true;

  if (!window.twq) {
    window.twq = createTwqStub();
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://static.ads-twitter.com/uwt.js';
    const first = document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(script, first);
  }

  window.twq?.('config', X_PIXEL_ID);
};

/** Fire Sign-up and wait so SPA navigation does not abort the beacon. */
export const trackXSignup = async (params?: {
  email?: string | null;
  conversionId?: string | null;
}): Promise<void> => {
  if (!shouldTrackXAds()) return;

  const email = params?.email ?? null;
  const conversionId = params?.conversionId ?? null;
  if (!markSignupTracked(signupDedupeKey(email, conversionId))) return;

  initXPixel();
  await waitForTwqReady();

  window.twq?.('event', X_SIGNUP_EVENT_ID, {
    value: null,
    currency: null,
    conversion_id: conversionId,
    email_address: email,
    phone_number: null,
  });

  await wait(FLUSH_MS);
};
